import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Star, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SurveyPage = () => {
  const { courseId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [surveyInfo, setSurveyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Estado para las respuestas
  const [responses, setResponses] = useState({});
  const [comments, setComments] = useState('');

  useEffect(() => {
    fetchSurveyInfo();
  }, [courseId, token]);

  const fetchSurveyInfo = async () => {
    if (!token) {
      setError('Token de encuesta no válido');
      setLoading(false);
      return;
    }
    
    try {
      const res = await axios.get(`${API_URL}/survey/${courseId}?token=${token}`);
      setSurveyInfo(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Encuesta no encontrada o ya no está activa');
    } finally {
      setLoading(false);
    }
  };

  const handleRatingChange = (itemId, rating) => {
    setResponses(prev => ({ ...prev, [itemId]: rating }));
  };

  const handleSubmit = async () => {
    // Verificar que al menos se haya respondido algo
    const hasResponses = Object.values(responses).some(v => v !== null);
    if (!hasResponses) {
      toast.error('Por favor, responde al menos una pregunta');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/survey/${courseId}/submit`, {
        course_id: courseId,
        token: token,
        ...responses,
        comentarios: comments || null
      });
      setSubmitted(true);
      toast.success('¡Gracias por completar la encuesta!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar la encuesta');
    } finally {
      setSubmitting(false);
    }
  };

  // Rating Stars Component
  const RatingStars = ({ itemId, value, onChange }) => {
    const [hovered, setHovered] = useState(null);
    
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(itemId, star)}
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(null)}
            className="p-1 transition-transform hover:scale-110 focus:outline-none"
          >
            <Star
              className={`w-8 h-8 transition-colors ${
                (hovered !== null ? star <= hovered : star <= (value || 0))
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
        {value && (
          <span className="ml-2 text-sm text-gray-500 self-center">
            {value}/5
          </span>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004E89]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">❌</span>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Encuesta no disponible</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">¡Gracias!</h1>
          <p className="text-gray-600 mb-4">
            Tu opinión es muy importante para nosotros y nos ayudará a mejorar nuestras formaciones.
          </p>
          <p className="text-sm text-gray-500">
            Ya puedes cerrar esta ventana.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-[#004E89] text-white rounded-t-xl p-6 text-center">
          <h1 className="text-2xl font-bold mb-2">Encuesta de Satisfacción</h1>
          <p className="text-blue-100">{surveyInfo?.course_name}</p>
        </div>
        
        {/* Intro */}
        <div className="bg-white border-x border-slate-200 p-6">
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              <strong>🔒 Esta encuesta es anónima.</strong> Tus respuestas nos ayudarán a mejorar la calidad de nuestras formaciones.
            </p>
          </div>
          
          <p className="text-gray-600 text-sm mb-4">
            Valora cada aspecto del 1 al 5, siendo 1 la puntuación más baja y 5 la más alta.
          </p>
        </div>
        
        {/* Questions */}
        <div className="bg-white border-x border-slate-200 divide-y divide-slate-100">
          {surveyInfo?.items?.map((item, index) => (
            <div key={item.id} className="p-6">
              <div className="flex items-start gap-4">
                <span className="flex-shrink-0 w-8 h-8 bg-[#004E89] text-white rounded-full flex items-center justify-center text-sm font-bold">
                  {index + 1}
                </span>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">{item.label}</h3>
                  <p className="text-sm text-gray-500 mb-3">{item.description}</p>
                  <RatingStars 
                    itemId={item.id} 
                    value={responses[item.id]} 
                    onChange={handleRatingChange}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Comments */}
        <div className="bg-white border-x border-slate-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-2">Comentarios adicionales (opcional)</h3>
          <p className="text-sm text-gray-500 mb-3">
            ¿Hay algo más que quieras compartir con nosotros?
          </p>
          <Textarea
            placeholder="Escribe aquí tus comentarios, sugerencias o cualquier observación..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={4}
            className="w-full"
          />
        </div>
        
        {/* Submit */}
        <div className="bg-slate-50 rounded-b-xl border border-slate-200 p-6">
          <Button 
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full bg-[#004E89] hover:bg-[#003B66] text-white py-6 text-lg"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Enviando...
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Enviar Encuesta
              </>
            )}
          </Button>
          <p className="text-center text-xs text-gray-500 mt-4">
            Al enviar, confirmas que tus respuestas son anónimas y serán utilizadas para mejorar nuestras formaciones.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SurveyPage;
